import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { validateAccount } from '../../core/validator/Validator'
import { env } from '../../core/Env'
import { rqx } from '../../core/request/API'
import { BrandedSplash } from '../../core/loader/BrandedSplash'
import { AddAccess } from './AddAccess'
import { RegisterAccess } from './RegisterAccess'
import { LinkEmailAccess } from './LinkEmailAccess'
import { goLogin } from './goLogin'

const NAMED_SSO_KEYS = new Set([
    'MP_SSO_RQX', 'MP_MARKET_CHECKOUT_SSO_RQX', 'MP_DIRECT_CHECKOUT_RQX', 'TOOL_MARKET_PREVIEW_SSO_RQX',
    'PCHT_SSO_RQX', 'PFSHP_SSO_RQX', 'PFSHP_SSO_DIRECT_RQX', 'PHRC_SSO_RQX', 'PHRC_SSO_DIRECT_RQX',
    'PFJOB_SSO_RQX', 'PFJOB_SSO_DIRECT_RQX', 'PFREFERRAL_SSO_RQX', 'PFREFERRAL_SSO_RQX_SSO_DIRECT_RQX',
    'PFPAYMESHARE_SSO_RQX', 'PFPAYMESHARE_SSO_DIRECT_RQX', 'PFHERO_SSO_RQX', 'PFHERO_SSO_DIRECT_RQX',
])

export const hasEntryAccessParam = (raw) =>
    raw !== null && raw !== undefined && `${raw}`.trim() !== ''

/**
 * ITU calling codes, longest-first so we strip e.g. 1264 before 1.
 * Used only to peel a leading country prefix from entryaccess mobiles.
 */
const CALLING_CODES_LONGEST_FIRST = [
    '1684', '1473', '1242', '1246', '1441', '1264', '1268', '1284', '1340', '1345',
    '1649', '1664', '1670', '1671', '1758', '1767', '1784', '1787', '1809', '1829',
    '1849', '1868', '1869', '1876', '1939',
    '998', '996', '995', '994', '993', '992', '977', '976', '975', '974', '973', '972', '971', '970',
    '968', '967', '966', '965', '964', '963', '962', '961', '960',
    '886', '880', '856', '855', '853', '852', '850',
    '692', '692', '689', '687', '686', '685', '683', '682', '681', '680', '679', '678', '677', '676', '675', '674', '673', '672', '670',
    '599', '598', '597', '595', '593', '592', '591', '590',
    '509', '508', '507', '506', '505', '504', '503', '502', '501', '500',
    '423', '421', '420',
    '389', '387', '386', '385', '383', '382', '381', '380', '378', '377', '376', '375', '374', '373', '372', '371', '370',
    '359', '358', '357', '356', '355', '354', '353', '352', '351', '350',
    '299', '298', '297', '291', '290',
    '269', '268', '267', '266', '265', '264', '263', '262', '261', '260',
    '258', '257', '256', '255', '254', '253', '252', '251', '250', '249', '248', '246', '245', '244', '243', '242', '241', '240',
    '239', '238', '237', '236', '235', '234', '233', '232', '231', '230', '229', '228', '227', '226', '225', '224', '223', '222', '221', '220',
    '218', '216', '213', '212', '211', '98', '95', '94', '93', '92', '91', '90',
    '86', '84', '82', '81', '66', '65', '64', '63', '62', '61', '60',
    '58', '57', '56', '55', '54', '53', '52', '51', '49', '48', '47', '46', '45', '44', '43', '41', '40',
    '39', '36', '34', '33', '32', '31', '30', '27', '20',
    '7', '1',
]

/**
 * Normalize entryaccess mobile:
 * - strips spaces / dashes / ()
 * - strips leading + or 00
 * - strips country calling code (PH +63 and other countries)
 * - PH: 09xxxxxxxxx → 9xxxxxxxxx (SI local form)
 */
export const normalizeEntryAccessMobile = (raw) => {
    let s = `${raw}`.trim().replace(/[\s\-().]/g, '')
    if (!s) return null

    const hadPlus = s.startsWith('+')
    if (hadPlus) s = s.slice(1)
    if (s.startsWith('00')) s = s.slice(2)

    if (!/^\d+$/.test(s)) return null

    // Already PH local
    if (s.length === 10 && s[0] === '9') return s
    if (s.length === 11 && s.startsWith('09')) return s.slice(1)

    // PH without +: 639272651018
    if (s.startsWith('63') && s.length === 12 && s[2] === '9') {
        return s.slice(2)
    }

    // Strip country code when intl form (+ / 00 / longer than local)
    if (hadPlus || s.length > 11) {
        for (const code of CALLING_CODES_LONGEST_FIRST) {
            if (!s.startsWith(code)) continue
            const rest = s.slice(code.length)
            if (rest.length < 8 || rest.length > 15) continue
            // PH national after 63
            if (code === '63' && rest.length === 10 && rest[0] === '9') return rest
            if (code === '63' && rest.length === 11 && rest.startsWith('09')) return rest.slice(1)
            return rest
        }
    }

    return s
}

const normalizeMobile = (value) => {
    const v = `${value}`.trim()
    if (v.length === 11 && v.startsWith('09')) return v.slice(1)
    return v
}

export const decodeEntryAccess = (raw) => {
    if (!hasEntryAccessParam(raw)) return null
    try {
        const decoded = window.atob(`${raw}`.trim().replace(/ /g, '+')).trim()

        // Email first (unchanged)
        if (validateAccount(decoded) === 'email') {
            return { value: decoded, va: 'email' }
        }

        // Mobile: strip +63 / other country codes, then accept
        const mobile = normalizeEntryAccessMobile(decoded)
        if (!mobile) return null

        const kind = validateAccount(mobile)
        if (kind === 'mobile_num') {
            return { value: normalizeMobile(mobile), va: 'mobile_num' }
        }
        // Non-PH national number after country strip (digits only, 8–15)
        if (/^\d{8,15}$/.test(mobile)) {
            return { value: mobile, va: 'mobile_num' }
        }

        return null
    } catch (err) {
        return null
    }
}

export const toolCategoryIsBusiness = (tool_category_id) => {
    try {
        const raw = typeof tool_category_id === 'string' ? JSON.parse(tool_category_id) : tool_category_id
        return raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, 'business')
    } catch (e) {
        return false
    }
}

const resolvePlatform = (q) => {
    const p = q.get('platform')
    if (p === 'personal') return 'psl'
    if (p === 'business') return 'biz'
    const seg = window.location.pathname.split('/').filter(Boolean)[0]
    return seg === 'psl' || seg === 'biz' ? seg : 'psl'
}

const routeEntryAccess = (ctx) => {
    const { useBizMobileFlow, accountExists, toolAccess, hasEmail, va } = ctx

    if (useBizMobileFlow) {
        if (!accountExists) {
            return { route: 'registration' }
        }
        if (!hasEmail) {
            return { route: 'link_email' }
        }
        if (toolAccess === 'no') {
            return { route: 'add_access' }
        }
        const loginAccount = ctx.linkedEmail || ctx.ainfo?.email
        const error = goLogin({
            ...ctx,
            account: loginAccount,
            va: 'email',
        })
        if (error) return { route: 'error', error }
        return { route: 'login' }
    }

    // email or personal mobile — standard routes
    if (!accountExists) {
        return { route: 'registration' }
    }
    if (toolAccess === 'no') {
        return { route: 'add_access' }
    }
    const error = goLogin({
        ...ctx,
        account: ctx.account,
        va: va || 'email',
    })
    if (error) return { route: 'error', error }
    return { route: 'login' }
}

const mapAttempt = (msg, hasToolAuth) => {
    if (!hasToolAuth) {
        return { accountExists: msg === 'valid', toolAccess: 'skipped' }
    }
    if (msg === 'valid') return { accountExists: true, toolAccess: 'yes' }
    if (msg === '!account') return { accountExists: true, toolAccess: 'no' }
    return { accountExists: false, toolAccess: 'no' }
}

export const EntryAccess = () => {
    const q = new URLSearchParams(window.location.search)
    const decoded = decodeEntryAccess(q.get('entryaccess'))
    const from = q.get('from')
    const callback = q.get('callback')
    const platformQuery = q.get('platform')
    const platform = resolvePlatform(q)
    const tidQuery = q.get('tid')
    const pricing = q.get('pricing')
    const subcb = q.get('subcb')
    const ref_a_frnd = q.get('ref_a_frnd')

    const [loading, setLoading] = useState(true)
    const [result, setResult] = useState({
        error: null,
        tool: null,
        authCallback: null,
        accountExists: null,
        toolAccess: null,
        attemptMsg: null,
        route: null,
        hasEmail: false,
        useBizMobileFlow: false,
        apiPlatform: platform,
        ainfo: null,
    })

    useEffect(() => {
        const run = async () => {
            if (!decoded) {
                setResult((prev) => ({ ...prev, error: 'entryaccess decode failed', route: 'error' }))
                setLoading(false)
                return
            }

            const { value: account, va } = decoded

            let toolAuth = null
            let tool = null
            let authCallback = null
            let redirected = false
            let toolIsBusiness = false

            try {
                if (from && NAMED_SSO_KEYS.has(from)) {
                    tool = { name: from, tid: tidQuery || null, type: 'named_sso' }
                    if (tidQuery) toolAuth = tidQuery
                } else if (from) {
                    const key = await rqx.get(
                        `<<global>>/${env() === 'local' ? 'dev' : env()}/tools/ssorqx`,
                        { tsa_key: from }
                    )

                    if (!key || key.msg === '!tk' || !key.tinfo?.[0]) {
                        setResult((prev) => ({
                            ...prev,
                            error: key?.msg === '!tk' ? 'TSA key does not exist' : 'TSA lookup failed',
                            route: 'error',
                        }))
                        return
                    }

                    const tinfo = key.tinfo[0]
                    toolIsBusiness = toolCategoryIsBusiness(tinfo.tool_category_id)
                    tool = {
                        name: tinfo.name,
                        tid: tinfo.tid,
                        type: tinfo.type,
                        logo: tinfo.logo,
                        tool_category_id: tinfo.tool_category_id,
                    }
                    if (callback && key.tu?.[0]) {
                        authCallback = key.tu[0][`${callback}_auth_callback`]?.value || null
                    }
                    if (tinfo.type !== 1) toolAuth = tinfo.tid
                } else if (tidQuery) {
                    tool = { name: 'tid query', tid: tidQuery, type: 'query' }
                    toolAuth = tidQuery
                }

                const useBizMobileFlow = va === 'mobile_num' && (platform === 'biz' || toolIsBusiness)
                const attemptPlatform = va === 'mobile_num'
                    ? (useBizMobileFlow ? 'biz' : 'psl')
                    : platform

                const attempt = await rqx.get(
                    `<<tool>>/${env()}/${attemptPlatform}/si/attempt/${va}`,
                    {
                        account,
                        ref: 1,
                        platform: attemptPlatform,
                        ...(toolAuth ? { tool_auth: toolAuth } : {}),
                    }
                )

                const attemptMsg = attempt?.msg ?? 'no response'
                const { accountExists, toolAccess } = mapAttempt(attemptMsg, !!toolAuth)
                const ainfo = attempt?.ainfo || null
                const hasEmail = Boolean(ainfo?.email)

                const routed = routeEntryAccess({
                    account,
                    email: va === 'email' ? account : ainfo?.email,
                    va,
                    platform,
                    apiPlatform: attemptPlatform,
                    platformQuery,
                    tool,
                    from,
                    callback,
                    authCallback,
                    accountExists,
                    toolAccess,
                    attemptMsg,
                    pricing,
                    subcb,
                    ref_a_frnd,
                    useBizMobileFlow,
                    hasEmail,
                    ainfo,
                    linkedEmail: ainfo?.email,
                })

                if (routed.route === 'login' && !routed.error) {
                    redirected = true
                    return
                }

                setResult({
                    error: routed.error || null,
                    tool,
                    authCallback,
                    accountExists,
                    toolAccess,
                    attemptMsg,
                    route: routed.route,
                    hasEmail,
                    useBizMobileFlow,
                    apiPlatform: attemptPlatform,
                    ainfo,
                })
            } catch (err) {
                setResult((prev) => ({
                    ...prev,
                    error: `request failed: ${err?.message || err}`,
                    route: 'error',
                }))
            } finally {
                if (!redirected) setLoading(false)
            }
        }

        run()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const account = decoded?.value
    const va = decoded?.va
    const sharedProps = {
        account,
        va,
        email: va === 'email' ? account : undefined,
        mobile: va === 'mobile_num' ? account : undefined,
        platform: result.apiPlatform || platform,
        platformQuery,
        tool: result.tool,
        authCallback: result.authCallback,
        from,
        subcb,
        pricing,
        ref_a_frnd,
        useBizMobileFlow: result.useBizMobileFlow,
        toolAccess: result.toolAccess,
        apiPlatform: result.apiPlatform || platform,
    }

    if (!loading && result.route === 'add_access') {
        return (
            <AddAccess
                mode="login"
                {...sharedProps}
                email={va === 'email' ? account : (result.ainfo?.email || account)}
            />
        )
    }

    if (!loading && result.route === 'registration') {
        return <RegisterAccess {...sharedProps} />
    }

    if (!loading && result.route === 'link_email') {
        return <LinkEmailAccess {...sharedProps} />
    }

    if (!loading && result.route === 'error') {
        return (
            <Box height="100vh" width="100%" display="flex" justifyContent="center" alignItems="center">
                <Box fontSize={14} color="error.main" textAlign="center" px={3}>
                    {result.error || 'Something went wrong'}
                </Box>
            </Box>
        )
    }

    return <BrandedSplash />
}
